import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "../../css/login.scss";
import "../../css/phoneInput.scss";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { message } from "antd";
import { manager_login } from "../../apis/ManagerLogin";
import { get_code } from "../../apis/GetCode";
import Cookies from "js-cookie";

const Login = () => {
  const navigate = useNavigate();
  const [loginType, setLoginType] = useState("phone");
  const [showPassword, setShowPassword] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [loading, setLoading] = useState(false); // 加载状态
  const [smsCode, setSmsCode] = useState(""); // 短信验证码
  const [countdown, setCountdown] = useState(0); // 倒计时
  const [tempToken, setTempToken] = useState(""); // 验证码（从 get_code 返回）

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    captcha: "", // 图形验证码输入
  });

  // 处理输入变化
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // 倒计时效果
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // 去除国家区号前缀
  const removeCountryCode = (phone) => {
    // 需要去掉国家码，只保留手机号部分
    if (!phone) return "";

    // 常见国家码列表
    const countryCodes = ["86", "1", "44", "81", "82", "852", "853", "886"];

    for (const code of countryCodes) {
      if (phone.startsWith(code)) {
        return phone.substring(code.length);
      }
    }

    return phone;
  };

  // 发送验证码
  const handleSendSmsCode = async () => {
    // 如果是手机登录，需要去除国家区号前缀
    const loginAccount =
      loginType === "phone" ? removeCountryCode(phoneNumber) : formData.email;
    const password = formData.password;

    // 验证必填项
    if (!loginAccount) {
      message.error(
        loginType === "phone" ? "请先输入手机号" : "请先输入邮箱/用户名"
      );
      return;
    }

    if (!password) {
      message.error("请先输入密码");
      return;
    }

    // 验证手机号格式
    if (loginType === "phone" && phoneNumber.length < 10) {
      message.error("请输入有效的手机号");
      return;
    }

    try {
      const response = await get_code(loginAccount, password);

      if (response && response.data && response.data.code === 200) {
        // 保存 temp_token，用于后续调用 manager_login
        const token = response.data.data?.temp_token;
        if (token) {
          setTempToken(token);
          // 将 temp_token 存入 cookie
          Cookies.set("temp_token", token, { expires: 7 });
          message.success("验证码已发送");
          setCountdown(60); // 开始60秒倒计时
        } else {
          message.error("获取验证码失败，请重试");
        }
      } else {
        message.error(response.data?.message || "发送验证码失败");
      }
    } catch (error) {
      console.error("发送验证码失败：", error);
      message.error(
        "发送验证码失败：" +
          (error.response?.data?.message || error.message || "网络错误")
      );
    }
  };

  // 登录处理
  const handleLogin = async () => {
    // 验证验证码
    if (!smsCode) {
      message.error("请输入验证码");
      return;
    }

    // 优先从 cookie 读取 temp_token，如果没有则使用 state
    const tokenToUse = tempToken || Cookies.get("temp_token");

    // 验证是否已获取临时令牌
    if (!tokenToUse) {
      message.error("请先获取验证码");
      return;
    }

    setLoading(true);

    try {
      const response = await manager_login(smsCode, tokenToUse);

      if (response && response.data && response.data.code === 200) {
        const { token, user } = response.data.data || {};

        if (token) {
          // 保存到 cookie（设置7天过期）
          Cookies.set("token", token, { expires: 7 });
          if (user) {
            Cookies.set("user", JSON.stringify(user), { expires: 7 });
          }

          // 清除 temp_token（登录成功后不再需要）
          Cookies.remove("temp_token");
          setTempToken("");

          message.success("登录成功！");

          // 跳转到主页或仪表板
          navigate("/userAccount");
        } else {
          message.error("登录失败：未获取到令牌");
        }
      } else {
        // 登录失败
        const errorMsg = response.data?.message || "登录失败";
        message.error(errorMsg);

        // 清空验证码和临时令牌，允许重新获取
        setSmsCode("");
        setTempToken("");
        Cookies.remove("temp_token");
      }
    } catch (error) {
      console.error("登录失败：", error);
      console.error("错误详情：", error.response);

      // 显示错误信息
      const errorMsg =
        error.response?.data?.message || error.message || "网络错误，请重试";
      message.error("登录失败：" + errorMsg);

      // 清空验证码和临时令牌
      setSmsCode("");
      setTempToken("");
      Cookies.remove("temp_token");
    } finally {
      setLoading(false);
    }
  };

  // 支持Enter键登录
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleLogin();
    }
  };

  return (
    <div className="login_box">
      <div className="login_box_left">
        <img alt="Login Background" src="/main/login.jpg" />
      </div>
      <div className="login_box_right">
        {/* 主登录区域 */}
        <div className="login_main_content">
          {/* Logo */}
          <div className="logo_section">
            <div className="logo_circle">
              <span>
                <img alt="" src="/main/login5.png" />
              </span>
            </div>
            <h1 className="app_title">Global E-Payment</h1>
          </div>

          {/* 登录方式切换 */}
          <div className="login_tabs">
            <div
              className={`tab ${loginType === "phone" ? "active" : ""}`}
              onClick={() => setLoginType("phone")}
            >
              手机登录
            </div>
            <div
              className={`tab ${loginType === "email" ? "active" : ""}`}
              onClick={() => setLoginType("email")}
            >
              邮箱/用户名登录
            </div>
          </div>

          {/* 登录表单 */}
          <div className="login_form">
            {/* 手机号/邮箱输入 */}
            <div className="input_group">
              {loginType === "phone" ? (
                <div className="input_wrapper phone_number_wrapper">
                  <img alt="" src="/main/login6-1.png" className="phone_icon" />
                  <PhoneInput
                    country={"cn"}
                    value={phoneNumber}
                    onChange={setPhoneNumber}
                    placeholder="手机号码"
                    inputClass="phone_number_input"
                    containerClass="phone_input_container"
                    buttonClass="phone_country_button"
                    dropdownClass="phone_dropdown"
                    enableSearch={true}
                    searchPlaceholder="搜索"
                    disableSearchIcon={false}
                    buttonText={(country) => `+${country.dialCode}`}
                    onKeyPress={handleKeyPress} // ✅ 添加Enter支持
                  />
                </div>
              ) : (
                <div className="input_wrapper">
                  <img alt="" src="/main/login6-6.png" />
                  <input
                    type="text"
                    className="form_input"
                    placeholder="登录邮箱/用户名"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    onKeyPress={handleKeyPress}
                  />
                </div>
              )}
            </div>

            {/* 密码输入 */}
            <div className="input_group">
              <div className="input_wrapper">
                <img alt="" src="/main/login6-2.png" />
                <input
                  type={showPassword ? "text" : "password"}
                  className="form_input"
                  placeholder="登录密码"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  onKeyPress={handleKeyPress}
                />
                <span
                  className="eye_icon"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <img alt="" src="/main/login6-5.png" />
                  ) : (
                    <img alt="" src="/main/login6-4.png" />
                  )}
                </span>
              </div>
            </div>

            <div className="input_group">
              <div className="input_wrapper sms_code_wrapper">
                <img alt="" src="/main/login6-3.png" />
                <input
                  type="text"
                  className="form_input"
                  placeholder="短信验证码"
                  value={smsCode}
                  onChange={(e) => setSmsCode(e.target.value)}
                  onKeyPress={handleKeyPress}
                  maxLength={6}
                />
                <button
                  className="send_code_btn"
                  onClick={handleSendSmsCode}
                  disabled={countdown > 0}
                  type="button"
                >
                  {countdown > 0 ? `${countdown}秒` : "发送验证码"}
                </button>
              </div>
            </div>

            {/* 登录按钮 */}
            <button
              className="login_button"
              onClick={handleLogin}
              disabled={loading} // ✅ 加载时禁用按钮
            >
              {loading ? "LOGIN..." : "LOGIN"} {/* ✅ 显示加载状态 */}
            </button>
          </div>
        </div>

        {/* 页脚版权 */}
        <div className="login_footer">
          <p>© 2022 GLOBAL E-PAYMENT LIMITED. All Rights Reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default Login;
